import { trimEnd } from 'lodash'
import BigNumber from 'bignumber.js'
import { toBigNumber, gt } from '~/utils/numbers'
import { PROTOCOL_TYPE } from './types'
import {
  TokenlonSDK,
  MAX_UINT160,
  CrossSwapOrderStatus,
} from '@tokenlon/v6-sdk'
import {
  Address,
  zeroAddress,
  createPublicClient,
  http,
  parseAbiItem,
  Hash,
  getAddress,
} from 'viem'
import dayjs from 'dayjs'
import { RateState } from './types'
import { Polygon, BSC } from '~/model/chain/ethereum'
import { getChainById } from '~/utils/chain'
import { compareString } from '~/utils/common'
import { IMTOKEN_WEB_PAYMASTER_TOKENS_BY_CHAIN } from '~/constants/imTokenWeb'

export const isAMM = (protocol: string): boolean => {
  return protocol?.includes(PROTOCOL_TYPE.AMM)
}

/**
 * 用户输入时，使用该方法进行格式化
 *  1. 保留用户输入的小数点 `.`
 *  2. 确保用户输入的小数点位数不超过 precision
 */
export const formatInputAmount = (amount, precision) => {
  try {
    const amountStr = amount?.toString()
    const [ints, decs] = amountStr?.split('.')

    return decs ? `${ints}.${decs?.substring(0, precision)}` : amountStr
  } catch (e) {
    return amount
  }
}

// @todo: @circlehotarux 通过参数控制加入 Math.min(8, decimal ?? 8) 的计算
export const formatAmount = (amount: any = '', precision = 8) => {
  try {
    if (amount === undefined || amount === null || amount === '') {
      return amount
    }
    const amountStr = toBigNumber(amount).toFixed()
    const [ints, decs] = amountStr?.split('.')
    if (precision === 0) {
      return ints
    }
    let trimmedDecs = trimEnd(decs?.substring(0, precision), '0')
    if (
      Number(trimmedDecs) === 0 &&
      Number(decs) !== 0 &&
      decs?.length > precision
    )
      trimmedDecs = decs

    return trimmedDecs ? `${ints}.${trimmedDecs}` : ints
  } catch (e) {
    return amount
  }
}

/**
 * eth 兑 token，是自主上链的形式，交易会扣除一定 eth 作为矿工费
 *  1. 在用户输入 eth 数量时，我们需要预留下 estimated gas fee，与 app 对齐，使用 extra?.tokenlon?.maxFeePerGas，而不是 1.5倍 baseFeePerGas
 *  2. 在用户点击闪兑时，我们需要使用 real time gas fee 进行校验
 * token 兑 token 时，不需要预留矿工费，也不需要进行矿工费校验
 */
export const DEFAULT_GAS_LIMIT = 580000

export const calcGasFee = (
  gasPrice: string,
  gasLimit: number | string,
): string =>
  toBigNumber(gasPrice).multipliedBy(toBigNumber(gasLimit)).toString()

export const validateInputAmount = ({
  balance,
  inputToken,
  inputAmount,
  order,
  t,
  returnRawError = false,
}) => {
  const insufficientBalanceError = returnRawError
    ? '$symbol_insufficient_balance'
    : t('$symbol_insufficient_balance', { symbol: inputToken?.symbol })
  if (!balance) {
    return insufficientBalanceError
  }
  if (!inputToken || !inputAmount || !order) return
  if (gt(inputAmount, balance)) return insufficientBalanceError
}

export const validateETHBalance = ({
  balance,
  gasFee,
  takerToken,
  takerTokenAmountUnit,
  t,
  isTakerETH,
  handleInsufficientBalance,
  returnRawError = false,
  disableSetPayMode,
}) => {
  const insufficientBalanceError = returnRawError
    ? '$symbol_insufficient_balance'
    : t('$symbol_insufficient_balance', { symbol: takerToken?.symbol })
  if (!balance) {
    return insufficientBalanceError
  }
  const cost = isTakerETH
    ? toBigNumber(takerTokenAmountUnit).plus(toBigNumber(gasFee))
    : toBigNumber(gasFee)

  if (gt(cost, balance)) {
    if (isTakerETH || disableSetPayMode) {
      return insufficientBalanceError
    } else {
      handleInsufficientBalance()
      return 'insufficient_balance_with_toggle_modal'
    }
  }
}

export const validateInputAmountSendBySelf = ({
  balance,
  inputToken,
  inputAmount,
  order,
  t,
  ethBalance,
  gasFee,
  imTokenWebEstimateGasFee,
  isTakerETH,
  handleInsufficientBalance,
  // 预览按钮的文案需要特殊处理，增加字段控制返回原始的错误信息
  returnRawError = false,
  isImTokenWeb,
  disableSetPayMode,
  isContractWallet,
  outputToken,
  chainId,
  inputTokenPrice,
  nativeTokenPrice,
  otherPaymasterTokens,
}) => {
  if (!inputToken || !inputAmount || !order) return

  // 校验输入代币余额是否足够
  if (gt(inputAmount, balance)) {
    return returnRawError
      ? '$symbol_insufficient_balance'
      : t('$symbol_insufficient_balance', { symbol: inputToken?.symbol })
  }

  // 参考 onMax 中的 imTokenWeb 逻辑进行校验
  if (isImTokenWeb && !isContractWallet) {
    // 优先级 1：imTokenWeb 账户未部署，使用 Sponsor 支付 gas，不预留任何代币（包括 ETH）
    // 只需要校验输入代币余额，上面已经校验过了
    return
  } else if (isImTokenWeb) {
    const reserveResult = getImTokenWebInputReserveResult({
      inputTokenAddress: inputToken?.address,
      outputTokenAddress: outputToken?.address,
      inputBalance: balance,
      estimateGasFee: imTokenWebEstimateGasFee,
      chainId,
      inputTokenPrice,
      nativeTokenPrice,
      otherPaymasterTokens,
    })

    if (reserveResult.error) {
      return returnRawError ? reserveResult.error : t(reserveResult.error)
    }

    if (reserveResult.reserveAmount) {
      const cost = toBigNumber(inputAmount).plus(reserveResult.reserveAmount)
      if (gt(cost.toString(), balance)) {
        return returnRawError
          ? 'imtoken_web_no_paymaster_available'
          : t('imtoken_web_no_paymaster_available')
      }
    }

    return
  } else {
    // EOA 钱包：只用 ETH 支付 gas，非 ETH 输入不需要预留
    const error = validateETHBalance({
      balance: ethBalance,
      gasFee,
      takerToken: inputToken,
      takerTokenAmountUnit: inputAmount,
      t,
      isTakerETH,
      handleInsufficientBalance,
      returnRawError,
      disableSetPayMode,
    })
    if (error) return error
  }
}

export const showSkeletonStateList = [
  RateState.LOADING,
  RateState.IDLE,
  RateState.NetworkReason,
]

export const getTokenPermit = async ({
  account,
  spender,
  tokenAddress,
  sdk,
}: {
  account: Address
  spender: Address
  tokenAddress: Address
  sdk: TokenlonSDK
}) => {
  const { nonce } = await sdk.permit.getTokenPermit({
    account,
    token: tokenAddress,
    spender: spender,
  })
  const permitMessage = {
    details: {
      token: tokenAddress,
      amount: MAX_UINT160,
      nonce,
      // expiration when the allowance need to be reset
      expiration: dayjs().add(1, 'month').unix(),
    },
    spender: spender,
    sigDeadline: BigInt(dayjs().add(30, 'minute').unix()),
  }
  const permit = await sdk.permit.signTakerPermit(permitMessage, account)
  return permit
}

const isPolygonOrBSC = (chainId: number) => {
  return chainId === Polygon.chainId || chainId === BSC.chainId
}

/**
 * 如果 inputToken 是 ETH 或 WETH，会有以下情况：
 * 1. recipient 是合约钱包，跨链收到的代币是 WETH，crossChain 只保留 destinationToken 是 WETH 的路径；
 * 2. recipient 是 EOA 钱包，跨链收到的代币是 ETH，crossChain 只保留 destinationToken 是 ETH 的路径；
 * 3. ETH 和 WETH 返回的 crossChain 属性同时包含 ETH 和 WETH 路径。
 * 特殊情况：
 * 1. 目标链是 polygon 或 bsc 时，recipient 不管是什么类型，收到的都是 WETH；
 * 2. 源链是 polygon 或 bsc 时，Across 只返回 WETH 路径；
 * 3. bsc 上的 ETH 0x2170ed0880ac9a755fd29b2688956bd959f933f8 是 Across 的 WETH，前后端也将该代币当做 WETH 处理。
 */
export const formatETHCrossChain = (
  crossChain: CrossChain,
  isContractAddress: boolean,
): CrossChain => {
  if (!crossChain) return []

  let filteredResult: CrossChain = []
  if (isContractAddress) {
    filteredResult = crossChain.filter(
      (item) => item.destinationTokenSymbol === 'WETH',
    )
  } else {
    filteredResult = crossChain.filter((item) => {
      if (isPolygonOrBSC(item.originChainId)) {
        // 源链是 polygon 或 bsc 时，Across 只返回 WETH 路径，不需要过滤
        return true
      } else if (isPolygonOrBSC(item.destinationChainId)) {
        // 目标链是 polygon 或 bsc 时，只有 WETH 路径有用
        return item.destinationTokenSymbol === 'WETH'
      } else {
        return item.destinationTokenSymbol === 'ETH'
      }
    })
  }
  // crossChain 属性来自 Across，Across 侧 ETH 地址返回的是 WETH 地址，需要做转化
  const formattedResult = filteredResult.map((item) => {
    // 源链是 polygon 或 bsc 时，只有 WETH 路径，但 EOA 地址收到的是 ETH，需要将地址转化成 Tokenlon 的 ETH 地址。但如果目标链是 polygon 或 bsc，则不需要转化
    if (
      !isContractAddress &&
      isPolygonOrBSC(item.originChainId) &&
      !isPolygonOrBSC(item.destinationChainId) &&
      item.destinationTokenSymbol === 'WETH'
    ) {
      return { ...item, destinationToken: zeroAddress }
    } else if (item.destinationTokenSymbol === 'ETH') {
      return { ...item, destinationToken: zeroAddress }
    } else {
      return item
    }
  })
  return formattedResult
}

const getClient = (chainId: number) => {
  const chain = getChainById(chainId)
  // https://eth-mainnet.token.im is not allowed to use eth_getLogs, so we use publicRpcUrl
  const rpcUrl = chain.publicRpcUrl ?? chain.rpcUrl
  return createPublicClient({
    transport: http(rpcUrl),
  })
}

const bytes32ToAddress = (bytes32: string): Address => {
  const address = `0x${bytes32.slice(-40)}`
  return getAddress(address)
}

const trackOriginTx = async ({
  fromChainId,
  fromTxHash,
  spokePoolAddress,
}: {
  fromChainId: number
  fromTxHash: string
  spokePoolAddress: Address
}) => {
  const fromClient = getClient(fromChainId)
  const receipt = await fromClient.waitForTransactionReceipt({
    hash: fromTxHash as Hash,
  })
  if (receipt.status === 'success') {
    const logs = receipt.logs
    // It's possible that the Across contract upgrade resulted in the depositId not being found.
    const FundsDepositedEvent = logs.find(({ address }) =>
      compareString(address, spokePoolAddress),
    )
    const depositId = FundsDepositedEvent?.topics?.[2]

    if (depositId) {
      console.log('FundsDepositedEvent depositId: ', BigInt(depositId))
      return {
        orderStatus: CrossSwapOrderStatus.OriginSuccess,
        depositId: BigInt(depositId),
      }
    } else {
      return {
        orderStatus: CrossSwapOrderStatus.OriginSuccess,
      }
    }
  } else {
    return {
      orderStatus: CrossSwapOrderStatus.OriginFailed,
    }
  }
}

const TRACKING_TIMEOUT_MS = 6000
const POLLING_INTERVAL_MS = 1000
const FilledRelayEvent = parseAbiItem(
  'event FilledRelay(bytes32 inputToken, bytes32 outputToken, uint256 inputAmount, uint256 outputAmount, uint256 repaymentChainId, uint256 indexed originChainId, uint256 indexed depositId, uint32 fillDeadline, uint32 exclusivityDeadline, bytes32 exclusiveRelayer, bytes32 indexed relayer, bytes32 depositor, bytes32 recipient, bytes32 messageHash, (bytes32 updatedRecipient, bytes32 updatedMessageHash, uint256 updatedOutputAmount, uint8 fillType) relayExecutionInfo)',
)

export const trackCrossChainTx = async ({
  fromTxHash,
  fromChainId,
  toChainId,
  recipient,
  spokePoolAddress,
  setOrderStatus,
  setIsTrackingCrossTxByEvent,
}: {
  fromTxHash: string
  fromChainId: number
  toChainId: number
  recipient: string
  spokePoolAddress: Address
  setOrderStatus: (status: CrossSwapOrderStatus) => void
  setIsTrackingCrossTxByEvent: (value: boolean) => void
}) => {
  try {
    let matchedEvent
    let depositId: bigint

    const toClient = getClient(toChainId)
    const startBlockNumber = await toClient.getBlockNumber()

    try {
      const { orderStatus, depositId: originDepositId } = await trackOriginTx({
        fromChainId,
        fromTxHash,
        spokePoolAddress,
      })

      setOrderStatus(orderStatus)

      if (
        orderStatus === CrossSwapOrderStatus.OriginSuccess &&
        !!originDepositId
      ) {
        depositId = originDepositId
      }
    } catch (error) {
      console.error('failed to get receipt', error)
    }

    if (!depositId) {
      setIsTrackingCrossTxByEvent(false)
      return
    }

    let timeout = false
    const timeoutId = setTimeout(() => {
      timeout = true
      setIsTrackingCrossTxByEvent(false)
    }, TRACKING_TIMEOUT_MS)

    while (!timeout && !matchedEvent) {
      const latestBlockNumber = await toClient.getBlockNumber()

      // Why not use publicClient.watchEvent(eth_newFilter)? Because some public RPCs restrict the use of this method. For example, https://mainnet.optimism.io returns 'rpc method is not whitelisted'.
      const logs = await toClient.getLogs({
        fromBlock: startBlockNumber,
        toBlock: latestBlockNumber,
        address: spokePoolAddress,
        event: FilledRelayEvent,
      })

      const matched = logs.find(({ args }) => {
        const logFromChainId = Number(args.originChainId)
        const logRecipient = bytes32ToAddress(args.recipient)
        const logDepositId = args.depositId

        return (
          logDepositId === depositId &&
          logFromChainId === fromChainId &&
          logRecipient === recipient
        )
      })

      if (matched) {
        matchedEvent = matched
        clearTimeout(timeoutId)
        setOrderStatus(CrossSwapOrderStatus.DestinationSuccess)
        break
      }

      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS))
    }
  } catch (error) {
    console.error('failed to track cross chain tx', error)
    setIsTrackingCrossTxByEvent(false)
  }
}

/**
 * 检查代币地址是否是 imTokenWeb 支持的 paymaster 代币
 */
export const isImTokenWebPaymasterToken = (
  tokenAddress: string | undefined,
  chainId: number,
): boolean => {
  if (!tokenAddress) return false
  if (compareString(tokenAddress, zeroAddress)) return true
  const supportedTokens = IMTOKEN_WEB_PAYMASTER_TOKENS_BY_CHAIN[chainId] || []
  return supportedTokens.some(
    (addr) => addr.toLowerCase() === tokenAddress.toLowerCase(),
  )
}

export const getImTokenWebInputReserveResult = ({
  inputTokenAddress,
  outputTokenAddress,
  inputBalance,
  otherPaymasterTokens,
  estimateGasFee,
  chainId,
  inputTokenPrice,
  nativeTokenPrice,
}: {
  inputTokenAddress?: string
  outputTokenAddress?: string
  inputBalance?: string
  otherPaymasterTokens?: { balance?: string; price?: string | number }[]
  estimateGasFee: string
  chainId: number
  inputTokenPrice?: string | number
  nativeTokenPrice?: string | number
}): { reserveAmount?: BigNumber; error?: string } => {
  if (isImTokenWebPaymasterToken(outputTokenAddress, chainId)) {
    return {}
  }

  const reserveAmount = toBigNumber(estimateGasFee)

  if (isImTokenWebPaymasterToken(inputTokenAddress, chainId)) {
    const balanceBN = toBigNumber(inputBalance || '0')
    const reserveAmountInInputToken = compareString(
      inputTokenAddress,
      zeroAddress,
    )
      ? reserveAmount
      : reserveAmount
          .multipliedBy(toBigNumber(nativeTokenPrice || '0'))
          .dividedBy(toBigNumber(inputTokenPrice || '0'))

    if (
      reserveAmountInInputToken.gt(0) &&
      balanceBN.gte(reserveAmountInInputToken)
    ) {
      return { reserveAmount: reserveAmountInInputToken }
    }
  }

  const hasEnoughOtherPaymasterToken = (otherPaymasterTokens || []).some(
    ({ balance, price }) => {
      const priceBN = toBigNumber(price || '0')
      if (priceBN.lte(0)) return false

      const reserveAmountInPaymasterToken = reserveAmount
        .multipliedBy(toBigNumber(nativeTokenPrice || '0'))
        .dividedBy(priceBN)

      return (
        reserveAmountInPaymasterToken.gt(0) &&
        toBigNumber(balance || '0').gte(reserveAmountInPaymasterToken)
      )
    },
  )

  if (hasEnoughOtherPaymasterToken) {
    return {}
  }

  return { error: 'imtoken_web_no_paymaster_available' }
}
